import { getIntegrationIconURL } from '@builder-legacy/integrations/utilities';

import Token from './token/token';
import LoopableToken from './token/loopable-token';
import UniversalToken from './token/universal-token';

/**
 * Parses tokens in strings
 */
export default class Tokens {
	/**
	 * Saves useful data in the instance, and creates all the tokens for a given field
	 *
	 * @param {String} itemType  The item type (trigger/action/user-selector)
	 * @param {String} fieldType The field type
	 * @param {Object} options   Optional options
	 * @return {undefined}
	 */
	constructor( itemType, fieldType = 'text', options = {} ) {
		this.setData();

		// Save data
		this.saveData( itemType, fieldType, options );
	}

	setData() {}

	/**
	 * Saves useful data in the token instance
	 *
	 * @param {String} itemType  The item type (trigger/action/user-selector)
	 * @param {String} fieldType The field type
	 * @param {Object} options   Optional options
	 * @return {undefined}
	 */
	saveData( itemType, fieldType, options ) {
		this.itemType = itemType;
		this.fieldType = fieldType;

		this.options = Object.assign(
			{
				includeGroupName: true,
				includeGroupIcon: true,
				includeGroupTokens: true,
				unsupportedTokens: [],
			},
			options
		);

		this.tokensThatRequireUser = [];
	}

	/**
	 * Gets all the tokens that can be used for a given item.
	 * These are also going to be filtered by fieldType
	 *
	 * @return {Array} Groups of tokens
	 */
	get tokens() {
		let tokensList = [];

		/**
		 * Get the loop tokens
		 * @handles LoopableToken
		 */
		const loopTokens = this.getLoopTokens();
		// Add tokens to the main array
		if ( loopTokens.tokens.length > 0 ) {
			tokensList.push( loopTokens );
		}

		/**
		 * Get the universal tokens
		 * @handles LoopableToken
		 */
		tokensList.push( ...this.getUniversalTokens() );

		/**
		 * Get the triggers tokens
		 * @handles LoopableToken
		 */
		const triggersTokens = this.getTriggersTokens();
		// Add tokens to the main array
		tokensList = [ ...tokensList, ...triggersTokens ];

		/**
		 * Get the action tokens
		 * Add them only to actions and the closure
		 * @handles LoopableToken
		 */
		if ( ! [ 'trigger', 'user-selector' ].includes( this.itemType ) ) {
			// Get the action tokens
			const actionsTokens = this.getActionsTokens();
			// Add tokens to the main array
			tokensList = [ ...tokensList, ...actionsTokens ];
		}

		return tokensList;
	}

	/**
	 * Filters tokens removing the unsupported ones.
	 * Unsupported tokens are defined using the "unsupportedTokens" property
	 *
	 * @param {Array} tokens The tokens
	 * @return {Array}         The tokens, filtered
	 */
	filterUnsupportedTokens( tokens = [] ) {
		// Check if there are unsupported tokens
		if ( ! _uo.utility.isEmpty( this.options.unsupportedTokens ) ) {
			// Filter them
			tokens = tokens.filter( ( token ) => {
				return ! this.options.unsupportedTokens.includes( token.id );
			} );
		}

		return tokens;
	}

	/**
	 * Return all the universal tokens that can be used a given item
	 *
	 * @return {Object} Group of tokens
	 */
	getUniversalTokens() {
		const universalTokens = [];

		/**
		 * Get the integrations that contain universal tokens
		 * @type {Object} Integrations, key is the integration ID
		 */
		const integrations = UncannyAutomator._core.directory.integrations.filterIntegrations( [
			UncannyAutomator._core.directory.integrations._filters.hasUniversalTokens,
		] );

		// Check if there are universal tokens available
		if ( _uo.utility.isEmpty( integrations ) ) {
			return [];
		}

		// Iterate the integrations alphabetically
		_uo.utility.sortArray.alphabetically(
			Object.entries( integrations ),
			'[1].name'
		).forEach( ( [ integrationCode, integration ] ) => {
			// Create the token group
			const integrationTokenGroup = {
				id: integrationCode,
				name: integration.name,
				tokens: []
			}

			// Add the icon
			if ( this.options.includeGroupIcon ) {
				integrationTokenGroup.icon = document.createElement( 'img' );
				integrationTokenGroup.icon.src = getIntegrationIconURL( { integrationCode: integrationCode } );
			}

			// Create child tokens, but filter those that are not supported
			integrationTokenGroup.tokens = this.filterUnsupportedTokens(
				integration.tokens
					.map( token => {
						// Check if the token requires user data
						if ( token.requiresUser ) {
							this.tokensThatRequireUser.push( token.id );
						}

						// Create and return the token
						return this.createTokenObject( token, integrationTokenGroup );
					} )
					.filter( token => token !== undefined )
			);

			// If the universal group has tokens, push it
			if ( integrationTokenGroup.tokens.length > 0 ) {
				// Append the token group
				universalTokens.push( integrationTokenGroup );
			}
		} );

		return universalTokens;
	}

	/**
	 * If the item is inside a loop, it will return the tokens of the loop
	 * 
	 * @return {Object} Group of tokens
	 */
	getLoopTokens() {
		// Create common tokens
		const loopTokens = {
			id: 'LOOP',
			tokens: [],
		};

		// We can only add the loop tokens if we have an item ID
		if ( _uo.utility.isEmpty( this.options.itemID ) ) {
			return loopTokens;
		}

		// Make sure this is not a loop-filter. 
		// Loop filters can't have loop tokens because they are not technically inside a loop.
		if ( this.itemType === 'loop-filter' ) {
			return loopTokens;
		}

		// Try to find the parent loop
		const parentLoop = UncannyAutomator._core.recipe.item.loop.getParentLoop( this.options.itemID );
	
		// If it's not inside a loop, then stop here
		if ( _uo.utility.isEmpty( parentLoop ) || _uo.utility.isEmpty( parentLoop.tokens ) ) {
			return loopTokens;
		}

		// Add the name
		if ( this.options.includeGroupName ) {
			loopTokens.name = _uo.utility.escHTML( wp.i18n.__( 'Loop', 'uncanny-automator' ) );
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			loopTokens.icon = document.createElement( 'uo-icon' );
			loopTokens.icon.setAttribute( 'id', 'repeat' );
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			// Iterate all the loop tokens
			parentLoop.tokens.forEach( token => {
				const tokenData = {
					id: token.id,
					type: token.data_type,
					name: token.name,
					cacheable: true,
					requiresUser: false,
				};

				loopTokens.tokens.push(
					// Add token using "LoopableToken" class
					this.createTokenObject( tokenData, loopTokens )
				);
			} );	
		}

		// Remove undefined
		loopTokens.tokens = loopTokens.tokens.filter( token => token !== undefined );

		// Filter tokens
		loopTokens.tokens = this.filterUnsupportedTokens( loopTokens.tokens );

		// Check what tokens require user data
		loopTokens.tokens.forEach( token => {
			// Check if it requires user data
			if ( token.requiresUser ) {
				this.tokensThatRequireUser.push( token.id );
			}
		} );

		return loopTokens;
	}

	/**
	 * Returns the tokens of all triggers
	 *
	 * @return {Array} Array with multiple groups of tokens
	 */
	getTriggersTokens() {
		// The tokens groups
		const groups = [];

		// The triggers
		const triggers = UncannyAutomator._core.recipe.item.getItemsTriggers();

		// Iterate triggers
		triggers.forEach( trigger => {
			// Get the trigger tokens
			const triggerGroup = this.getItemTokens( trigger );

			// Check if it has tokens
			if ( triggerGroup.tokens.length > 0 || ! this.options.includeGroupTokens ) {
				groups.push( triggerGroup );
			}
		} );

		return groups;
	}

	/**
	 * Returns the tokens from the actions. If there is an item ID defined, it will filter tokens 
	 * so it returns only those available for that particular action
	 * 
	 * @return {Array} with multiple group of tokens
	 */
	getActionsTokens() {
		// The tokens groups
		const tokenGroups = [];

		// Determine the action retrieval parameters based on item type and item ID
		const isValidItemType = [ 'condition', 'action', 'closure', 'loop', 'loop-filter' ].includes( this.itemType );
		const hasValidItemID = ! _uo.utility.isEmpty( this.options.itemID );

		// Get the actions
		const actions = isValidItemType && hasValidItemID
			? UncannyAutomator._core.recipe.item.getItemsActions( {
				beforeActionID: this.options.itemID,
				exclude: [ this.options.itemID ]
			} )
			: UncannyAutomator._core.recipe.item.getItemsActions();

		// Iterate actions
		actions.forEach( action => {
			// Get the item tokens
			const actionTokens = this.getItemTokens( action );

			// Check if it has tokens
			if ( actionTokens.tokens.length > 0 || ! this.options.includeGroupTokens ) {
				tokenGroups.push( actionTokens );
			}
		} );
		
		return tokenGroups;
	}

	/**
	 * Get the tokens of an item. This is using the new ._recipe object
	 *
	 * @param {Object} item The item
	 * @return {Object}     Group of tokens
	 */
	getItemTokens( item ) {
		// Create the tokens group
		const tokensGroup = {
			id: item.code,
			integrationId: item.integration_code,
			itemID: item.id,
			itemType: item.type,
		};

		// Add the group name
		if ( this.options.includeGroupName ) {
			// Get the sentence
			const itemDirectory = UncannyAutomator._core.directory.items.getItem(
				item.integration_code,
				item.type,
				item.code
			);

			// If the global sentence is defined, use that
			if ( itemDirectory?.sentence?.dynamic ) {
				tokensGroup.name = itemDirectory.sentence.dynamic;
			} else {
				// Otherwise, use the sentence
				tokensGroup.name = item?.backup?.sentence;
			}
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			// Create the icon
			const $icon = document.createElement( 'img' );
			$icon.src = getIntegrationIconURL( { integrationCode: item.integration_code } );

			// Add the icon
			tokensGroup.icon = $icon;
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			let itemTokens = [];

			if ( item.tokens ) {
				// Add all tokens
				item.tokens.forEach( token => {
					itemTokens.push( 
						this.createTokenObject( 
							{
								id: token.id,
								// We're using a different syntax here, so we have to consolidate it
								type: token.token_type === 'loopable' ? 'loopable' : token.data_type,
								name: token.name,
								cacheable: true,
								itemID: item.id
							},

							tokensGroup 
						) 
					);
				} );

				// Filter undefined tokens
				itemTokens = itemTokens.filter( token => token !== undefined );
			}

			// Add the tokens to the group
			tokensGroup.tokens = itemTokens;

			// Filter tokens
			tokensGroup.tokens = this.filterUnsupportedTokens( tokensGroup.tokens );

			// Filter tokens using a custom function (if it has one)
			if ( ! _uo.utility.isEmpty( item.filterTokens ) ) {
				// Try to overwrite empty function
				try {
					// Try to convert the string to function, but first remove the <script> tags and trim the string
					// eslint-disable-next-line no-eval
					const filterTokens = eval(
						`(${ item.filterTokens.replace( '<script>', '' ).replace( '</script>', '' ).trim() })`
					);

					// Filter the tokens
					itemTokens = filterTokens( tokensGroup, item );
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.warn( e );
						// eslint-disable-next-line no-console
						console.log( item.filterTokens );
					}
				}
			}
		}

		// Add the group of tokens
		return tokensGroup;
	}

	/**
	 * Creates a token based on the provided token data and the onlyLoopableTokens flag.
	 *
	 * @param {Object} tokenData The data for the token.
	 * @param {String} tokenData.id The token ID.
	 * @param {String} tokenData.type The token type.
	 * @param {String} tokenData.name The token name.
	 * @param {Boolean} tokenData.cacheable Whether the token is cacheable.
	 * @param {Boolean} tokenData.requiresUser Whether the token requires user data.
	 * @param {Object} group The group to which the token belongs.
	 * @returns {(Token|LoopableToken|null)} The created token or null if the token should not be added.
	 */
	createTokenObject( tokenData, group ) {
		if ( this.options?.onlyLoopableTokens ) {
			// Scenario: this.options?.onlyLoopableTokens is true and token type is 'loopable'
			if ( tokenData.type === 'loopable' ) {
				// Add token using "LoopableToken" class
				return new LoopableToken( tokenData )
			}

			// Scenario: this.options?.onlyLoopableTokens is true and token type is not 'loopable'
			if ( this.options?.forceShowBothLoopableAndNonLoopable ) {
				// console.log( tokenData );

				// Check if the token has fields and create a UniversalToken
				if ( ! _uo.utility.isEmpty( tokenData.fields ) ) {
					return new UniversalToken( tokenData.id );
				}

				// Add token using "Token" class
				return new Token( tokenData )
			}
		} else {
			// Scenario: this.options?.onlyLoopableTokens is false and token type is not 'loopable'
			if ( tokenData.type !== 'loopable' ) {
				// console.log( tokenData );

				// Check if the token has fields and create a UniversalToken
				if ( ! _uo.utility.isEmpty( tokenData.fields ) ) {
					return new UniversalToken( tokenData.id );
				}

				// Add token using "Token" class
				return new Token( tokenData )
			}

			// Scenario: this.options?.onlyLoopableTokens is false and token type is 'loopable'
			if ( this.options?.forceShowBothLoopableAndNonLoopable ) {
				// Add token using "LoopableToken" class
				return new LoopableToken( tokenData )
			}
		}
	}

	/**
	 * Filters the tokens based on the field type
	 *
	 * @param {Array} tokens The tokens to be filtered
	 * @return {Array} tokens The tokens, filtered
	 */
	filterTokensByType( tokens ) {
		// Check if the field type is defined
		// Otherwise, don't filter the tokens
		if ( _uo.utility.isDefined( this.fieldType ) ) {
			// Check if it's NOT a text, textarea or url field
			// We don't have to filter tokens for those
			if ( ! [ 'text', 'textarea', 'url' ].includes( this.fieldType ) ) {
				// Iterate the tokens
				tokens = tokens.filter( ( token ) => {
					// Get the token type
					let tokenType = token.type;

					// Check if the field is a "float" field. In that case,
					// promote all the "int" tokens to "float". All integers
					// are floats
					if ( this.fieldType == 'float' && tokenType == 'int' ) {
						tokenType = 'float';
					}

					// Check if the token type matches the field type
					return this.fieldType == tokenType;
				} );
			}
		}

		return tokens;
	}

	/**
	 * Creates a DOM node with the token data
	 *
	 * @param {String} tokenID A token ID
	 * @return {Node}           The token node
	 */
	createTokenDOMNode( tokenID ) {
		// Default response
		let $tokenNode = null;

		// Get more information about the token
		const token = this.getTokenDataByID( tokenID );

		// Check if the token is valid
		if ( _uo.utility.isDefined( token ) ) {
			// Check if we already created this element
			if ( _uo.utility.isDefined( UncannyAutomator._cache.tokens[ token.id ] ) ) {
				$tokenNode = UncannyAutomator._cache.tokens[ token.id ];
			} else {
				// Create the token node
				// VanillaJS because of the performance, this might be heavy
				$tokenNode = document.createElement( 'span' );
				$tokenNode.classList.add( 'uap-token' );

				// Create icon
				const $tokenIcon = document.createElement( 'span' );
				$tokenIcon.classList.add( 'uap-token__icon' );
				$tokenIcon.appendChild( token.group.icon.cloneNode() );

				// Insert the icon
				$tokenNode.appendChild( $tokenIcon );

				// Insert the name
				const $tokenName = document.createElement( 'span' );
				$tokenName.classList.add( 'uap-token__name' );
				$tokenName.innerHTML = token.name;
				$tokenNode.appendChild( $tokenName );

				// Check if this is a item token
				if ( _uo.utility.isDefined( token.itemID ) ) {
					// Save the token action
					const tokenAction = UncannyAutomator.recipe.items[ token.itemID ];

					// Check if we have to add the item ID
					if ( tokenAction.shouldShowItemId ) {
						$tokenName.insertAdjacentHTML( 'afterBegin', `<span class="uap-token__id">${ _uo.utility.sprintf( '#%s: ', token.itemID ) }</span>` );
					}

					// Check if it's an action
					if ( tokenAction.itemType === 'action' ) {
						$tokenNode.classList.add( 'uap-token--action' );

						const $flags = document.createElement( 'span' );
						$flags.classList.add( 'uap-token__flags' );

						// Check if the action is inside a condition block
						if ( tokenAction.hasConditions ) {
							$tokenNode.classList.add( 'uap-token--action-conditions', 'uap-token--warning' );

							$flags.insertAdjacentHTML( 
								'beforeEnd', 
								`<uo-icon id="code-merge" slot="target"></uo-icon>` 
							);
						}

						// Check if this is being added to a field
						if ( 
							_uo.utility.isDefined( this.Field ) &&
							_uo.utility.isDefined( this.Field.attributes.itemId ) &&
							_uo.utility.isDefined( UncannyAutomator.recipe.items[ this.Field.attributes.itemId ] )
						) {
							this.destinationItemID = this.Field.attributes.itemId;
						}

						// Check if the destination is an action
						if ( _uo.utility.isDefined( this.destinationItemID ) &&
							_uo.utility.isDefined( UncannyAutomator.recipe.items[ this.destinationItemID ] ) &&
							UncannyAutomator.recipe.items[ this.destinationItemID].itemType === 'action'
						) {
							// Save the destination action
							const destinationItem = UncannyAutomator.recipe.items[ this.destinationItemID ];

							// Check if the action where the token is coming from is scheduled, BUT THE DESTINATION action is not
							// This means the token MIGHT not be available
							if ( tokenAction.isScheduled && ! destinationItem.isScheduled ) {
								$tokenNode.classList.add( 'uap-token--action-scheduled', 'uap-token--warning' );

								$flags.insertAdjacentHTML( 
									'beforeEnd', 
									`<uo-icon id="clock" slot="target"></uo-icon>` 
								);
							}

							if ( tokenAction.isDelayed && ! destinationItem.isDelayed ) {
								$tokenNode.classList.add( 'uap-token--action-delayed', 'uap-token--warning' );

								$flags.insertAdjacentHTML( 
									'beforeEnd', 
									`<uo-icon id="clock" slot="target"></uo-icon>` 
								);
							}

							if ( tokenAction.isDelayedCustom && ! destinationItem.isDelayedCustom ) {
								$tokenNode.classList.add( 'uap-token--action-delayed', 'uap-token--warning' );

								$flags.insertAdjacentHTML( 
									'beforeEnd', 
									`<uo-icon id="clock" slot="target"></uo-icon>` 
								);
							}
						}

						$tokenNode.append( $flags );
					}
				}

				// Check if we can save the token in the cache
				if ( token.cacheable ) {
					// Save node in the cache
					UncannyAutomator._cache.tokens[ token.id ] = $tokenNode;
				}
			}

			// Clone the node
			$tokenNode = $tokenNode.cloneNode( true );
		} else {
			// Create the token node
			// VanillaJS because of the performance, this might be heavy
			$tokenNode = document.createElement( 'span' );
			$tokenNode.classList.add( 'uap-token', 'uap-token--invalid' );

			// Insert the icon
			const $tokenIcon = document.createElement( 'uo-icon' );
			$tokenIcon.setAttribute( 'id', 'exclamation-triangle' );
			$tokenNode.appendChild( $tokenIcon );

			// Insert the name
			const $tokenName = document.createElement( 'span' );
			$tokenName.classList.add( 'uap-token__name' );
			$tokenName.innerHTML = tokenID;
			$tokenNode.appendChild( $tokenName );
		}

		// @to-do Add event listener to edit the token
		// $tokenNode.addEventListener( 'click', () => {
		// 	// Check if it starts with {{UT
		// 	if ( ! tokenID.startsWith( 'UT' ) ) {
		// 		return;
		// 	}

		// 	// Edit the universal token
		// 	const universalToken = new UniversalToken( tokenID );

		// 	// Edit the dialog
		// 	universalToken.renderOptions( detail => {
		// 		console.log( detail.updatedID );
		// 	} );
		// } );

		return $tokenNode;
	}

	/**
	 * Returns more data about a token by just using the token ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {Object}        The token data
	 */
	getTokenDataByID( tokenID = '' ) {
		// Default response
		let token = null;

		// Check if the tokenID is not empty
		if ( ! _uo.utility.isEmpty( tokenID ) ) {
			// Iterate the groups of tokens
			this.tokens.some( ( tokensGroup ) => {
				// Get the token using the tokenID
				token = tokensGroup.tokens.reduce( ( locatedToken, currentToken ) => {
					if ( _uo.utility.isDefined( currentToken.is ) ) {
						return currentToken.is( tokenID ) ? currentToken : locatedToken;
					} else {
						return currentToken.id == tokenID ? currentToken : locatedToken;
					}
				}, null );

				// Check if we found a token
				if ( _uo.utility.isDefined( token ) ) {
					// Check if it has a variation generator
					if ( _uo.utility.isDefined( token ) && _uo.utility.isDefined( token.getVariation ) ) {
						token = token.getVariation( tokenID );
					}

					// Add more data to the token
					token.group = {
						id: tokensGroup.id,
						name: tokensGroup.name,
						icon: tokensGroup.icon,
					};

					return true;
				} else {
					return false;
				}
			} );
		}

		return token;
	}

	/**
     * Renders text by replacing tokens with their corresponding HTML elements.
     * This method handles tokens at all levels, replacing them recursively from the deepest level first.
     * 
     * @param {String} textWithTokens - The initial text containing tokens to be replaced.
     * @param {Object} options - Configuration options for rendering.
     * @returns {String|HTMLElement} The rendered text with all tokens replaced, returned as a string or a DOM node based on options.
     */
    renderTextWithTokens( textWithTokens = '', options = {} ) {
        options = Object.assign( {
            template: '',
            parseTokensInAttributes: true,
            returnString: false,
            wrapperClass: 'uap-text-with-tokens'
        }, options );

        // Recursively replace tokens from the deepest level
        const replacedText = this.recursivelyReplaceTokens( textWithTokens, options );

        // Return based on options
        return this.returnBasedOnOption( replacedText, options );
    }

	/**
     * Recursively replaces tokens in the text, starting from the deepest level.
     * 
     * @private
     * @param {string} text - The text to process for token replacement.
     * @param {Object} options - Configuration options for replacement.
     * @returns {string} The text with all tokens replaced.
     */
    recursivelyReplaceTokens( text, options ) {
        // Extract top-level tokens using the provided utility function
        const tokens = _uo.utility.getTokenIDsFromString( text, options.parseTokensInAttributes );

        // If no tokens are found, return the text as is
        if ( tokens.length === 0 ) {
            return text;
        }

        // Replace each token with its HTML representation
        let processedText = text;
        tokens.forEach( token => {
            // Create the HTML element for the token
            const $token = this.createTokenDOMNode( token );
            let tokenHTML = $token.outerHTML;

            // Wrap the token HTML in a template, if provided
            if ( options.template ) {
                tokenHTML = _uo.utility.sprintf( options.template, tokenHTML, token );
            }

            // Replace the token in the text
            processedText = processedText.replace( `{{${ token }}}`, tokenHTML );
        });

        // Recursively process the text to replace nested tokens
        return this.recursivelyReplaceTokens( processedText, options );
    }

	/**
     * Returns the processed text as specified by the options: either as a string or wrapped in a DOM node.
     * 
     * @param {string} textWithTokens - The text to return, with tokens already replaced.
     * @param {Object} options - Options specifying the return format.
     * @returns {string|HTMLElement} The processed text according to the specified return format in options.
     */
    returnBasedOnOption( textWithTokens, options ) {
        if ( options.returnString ) {
            return textWithTokens;
        }

		const $container = document.createElement( 'span' );
		$container.classList.add( options.wrapperClass );
		$container.innerHTML = textWithTokens;
		return $container;
    }
}
